import { isEmpty } from "lodash";
import { FieldInputProps } from "react-final-form";

import { IMacroAction } from "store/entities/macros/interfaces";
import { IRuleAction } from "store/entities/rules/interfaces";

import { ActionFieldArrayElement } from "./interfaces";
import { ActionType } from "./types";

export const ACTION_EMPTY_STATE_TEXT = "Select the actions to be performed.";

export const changeAction = (
  input: FieldInputProps<ActionFieldArrayElement>,
  newAction: Partial<IMacroAction>
): void => {
  input.onChange(newAction);
};

export const changeActionValues = (
  input: FieldInputProps<ActionFieldArrayElement>,
  newValues: Partial<IMacroAction>,
  options: { overwrite: boolean } = { overwrite: false }
): void => {
  input.onChange(options.overwrite ? { type: input.value.type, ...newValues } : { ...input.value, ...newValues });
};

export function isActionType<T extends IRuleAction>(
  input: FieldInputProps<ActionFieldArrayElement>,
  type: T["type"]
): input is FieldInputProps<T> {
  return input.value.type === type;
}

export const defaultActions: Record<ActionType, Partial<IRuleAction>> = {
  [ActionType.Assign]: { type: "assign" },
  [ActionType.FeedbackRequest]: { type: "sendRatingRequest", template: { text: "" } },
  [ActionType.FollowUp]: { type: "sendFollowUp", template: { text: "" } },
  [ActionType.Message]: { type: "message", isPrivate: false, template: { text: "" } },
  [ActionType.PrivateNote]: { type: "message", isPrivate: true, template: { text: "" } },
  [ActionType.Status]: { type: "status" },
  [ActionType.Priority]: { type: "priority" },
  [ActionType.AddTags]: { type: "addTags", tagIDs: [] },
  [ActionType.RemoveTags]: { type: "removeTags", tagIDs: [] },
  [ActionType.Spam]: { type: "spam", spam: true },
  [ActionType.Unspam]: { type: "spam", spam: false },
  [ActionType.AddFollowers]: { type: "addFollowers", followers: [] },
  [ActionType.RemoveFollowers]: { type: "removeFollowers", followers: [] },
};

export const mapActionToActionType = (action: ActionFieldArrayElement): ActionType | undefined => {
  if (isEmpty(action)) {
    return undefined;
  }

  switch (action.type) {
    case "spam":
      return action.spam ? ActionType.Spam : ActionType.Unspam;
    case "message":
      return action.isPrivate ? ActionType.PrivateNote : ActionType.Message;
    case "addTags":
      return ActionType.AddTags;
    case "removeTags":
      return ActionType.RemoveTags;
    case "status":
      return ActionType.Status;
    case "priority":
      return ActionType.Priority;
    case "sendRatingRequest":
      return ActionType.FeedbackRequest;
    case "sendFollowUp":
      return ActionType.FollowUp;
    case "assign":
      return ActionType.Assign;
    case "addFollowers":
      return ActionType.AddFollowers;
    case "removeFollowers":
      return ActionType.RemoveFollowers;
    default:
      // eslint-disable-next-line no-console
      console.error("Unknown action type", action);
      return undefined;
  }
};
